import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  Card,
  CardContent,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  SimCard as SimCardIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  DataUsage as DataUsageIcon,
  SignalWifi4Bar as SignalIcon,
  SignalWifiOff as SignalOffIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { SimCard } from '../../types';
import { formatDate, formatDataUsage, getDaysUntilExpiration, getStatusColor, getStatusLabel, getDeviceDisplayName } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';

interface SimCardRowProps {
  simCard: SimCard;
  onEdit: (simCard: SimCard) => void;
  onView: (simCard: SimCard) => void;
  onUpdateStatus: (simCard: SimCard, status: SimCard['status']) => void;
  onSyncWithSimbase: (simCard: SimCard) => void;
}

const SimCardRow: React.FC<SimCardRowProps> = ({ simCard, onEdit, onView, onUpdateStatus, onSyncWithSimbase }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getExpirationStatus = () => {
    if (!simCard.expirationDate) return { color: 'text.secondary', label: 'Sin fecha', days: null };

    const days = getDaysUntilExpiration(simCard.expirationDate);

    if (days < 0) return { color: 'error.main', label: `Vencido hace ${Math.abs(days)} días`, days };
    if (days === 0) return { color: 'error.main', label: 'Vence hoy', days };
    if (days <= 7) return { color: 'warning.main', label: `Vence en ${days} días`, days };
    if (days <= 30) return { color: 'info.main', label: `Vence en ${days} días`, days };
    return { color: 'success.main', label: `Vence en ${days} días`, days };
  };

  const getDataUsagePercentage = () => {
    const currentPeriod = simCard.dataUsage?.currentPeriod;
    if (!currentPeriod?.used) return 0;

    // Asegurar que ambos valores estén en MB para el cálculo
    let usedMB = currentPeriod.used;
    let limitMB = simCard.plan?.dataLimit || 30; // Usar límite del plan o 30MB por defecto

    // Si los datos están en bytes, convertir a MB
    // Asumiendo que valores > 100,000 están probablemente en bytes
    if (usedMB > 100000) {
      usedMB = usedMB / (1024 * 1024); // bytes a MB
    }
    if (limitMB > 100000) {
      limitMB = limitMB / (1024 * 1024); // bytes a MB
    }

    return Math.min((usedMB / limitMB) * 100, 100);
  };

  const getDataUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'success';
  };

  const expirationStatus = getExpirationStatus();
  const dataUsagePercentage = getDataUsagePercentage();
  const dataUsageColor = getDataUsageColor(dataUsagePercentage);

  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: getStatusColor(simCard.status) }}>
            <SimCardIcon />
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {simCard.carrier}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ICCID: {simCard.iccid}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        {simCard.phoneNumber ? (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {simCard.phoneNumber}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin número
          </Typography>
        )}
        {simCard.plan?.name && (
          <Typography variant="body2" color="text.secondary">
            Plan: {simCard.plan.name}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {typeof simCard.clientId === 'object' && simCard.clientId ? simCard.clientId.name : 'Sin asignar'}
        </Typography>
        {typeof simCard.deviceId === 'object' && simCard.deviceId && (
          <Typography variant="body2" color="text.secondary">
            GPS: {getDeviceDisplayName(simCard.deviceId)}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={getStatusLabel(simCard.status)}
            sx={{
              backgroundColor: getStatusColor(simCard.status) + '20',
              color: getStatusColor(simCard.status),
              border: `1px solid ${getStatusColor(simCard.status)}`,
            }}
            size="small"
          />
          {simCard.status === 'active' ? (
            <Tooltip title="Activa">
              <SignalIcon sx={{ color: 'success.main', fontSize: 20 }} />
            </Tooltip>
          ) : (
            <Tooltip title="Inactiva">
              <SignalOffIcon sx={{ color: 'error.main', fontSize: 20 }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {simCard.dataUsage?.currentPeriod?.used !== undefined ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatDataUsage(simCard.dataUsage?.currentPeriod?.used || 0)} / {formatDataUsage((simCard.plan?.dataLimit || 30) * 1024 * 1024)}
              </Typography>
              <Typography variant="caption" color={`${dataUsageColor}.main`}>
                ({dataUsagePercentage.toFixed(0)}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={dataUsagePercentage}
              color={dataUsageColor}
              sx={{ height: 6, borderRadius: 3 }}
            />
            {!simCard.plan?.dataLimit && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Límite por defecto: 30MB
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin datos de uso
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            color: expirationStatus.color,
            fontWeight: expirationStatus.days !== null && expirationStatus.days <= 7 ? 600 : 400,
          }}
        >
          {expirationStatus.label}
        </Typography>
      </TableCell>
      <TableCell>
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { onView(simCard); handleMenuClose(); }}>
            <ViewIcon sx={{ mr: 1 }} fontSize="small" />
            Ver Detalles
          </MenuItem>
          <MenuItem onClick={() => { onEdit(simCard); handleMenuClose(); }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Editar
          </MenuItem>
          <MenuItem onClick={() => { onSyncWithSimbase(simCard); handleMenuClose(); }}>
            <RefreshIcon sx={{ mr: 1 }} fontSize="small" />
            Sincronizar Simbase
          </MenuItem>
          <MenuItem onClick={() => { onUpdateStatus(simCard, simCard.status === 'active' ? 'inactive' : 'active'); handleMenuClose(); }}>
            <SettingsIcon sx={{ mr: 1 }} fontSize="small" />
            {simCard.status === 'active' ? 'Desactivar' : 'Activar'}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export const SimCardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const {
    data: simCardsData,
    loading,
    error,
    execute: fetchSimCards,
  } = useApi<{ simCards: SimCard[]; total: number; globalTotal: number; globalStats: any; pages: number }>(
    () => apiService.getSimCards(page + 1, rowsPerPage, searchTerm || undefined, statusFilter !== 'all' ? statusFilter : undefined),
    { immediate: false }
  );

  const { execute: updateStatus, loading: updating } = useAsyncOperation();
  const { execute: syncWithSimbase, loading: syncing } = useAsyncOperation();

  useEffect(() => {
    fetchSimCards();
  }, [page, rowsPerPage, searchTerm, statusFilter, fetchSimCards]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (simCard: SimCard) => {
    navigate(`/simcards/${simCard._id}`);
  };

  const handleEdit = (simCard: SimCard) => {
    navigate(`/simcards/${simCard._id}/edit`);
  };

  const handleUpdateStatus = async (simCard: SimCard, status: SimCard['status']) => {
    const result = await updateStatus(() => apiService.updateSimCardStatus(simCard._id, status));
    if (result) {
      fetchSimCards();
    }
  };

  const handleSyncWithSimbase = async (simCard: SimCard) => {
    const result = await syncWithSimbase(() => apiService.syncSimCardWithSimbase(simCard._id));
    if (result) {
      fetchSimCards();
    }
  };

  const handleAddSimCard = () => {
    navigate('/simcards/new');
  };

  const getFilteredSimCards = () => {
    if (!simCardsData?.simCards) return [];
    return simCardsData.simCards;
  };

  const getStatusCounts = () => {
    if (!simCardsData?.globalStats) return { total: 0, active: 0, inactive: 0, expired: 0, expiringSoon: 0 };

    // Usar estadísticas globales del backend
    const { globalStats } = simCardsData;
    const simCards = simCardsData.simCards;

    // Solo calcular "expiringSoon" desde los datos actuales ya que requiere lógica de fecha
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const expiringSoon = simCards ? simCards.filter(s =>
      s.expirationDate &&
      new Date(s.expirationDate) <= thirtyDaysFromNow &&
      new Date(s.expirationDate) > now &&
      s.status === 'active'
    ).length : 0;

    return {
      total: globalStats.total || 0,
      active: globalStats.active || 0,
      inactive: globalStats.inactive || 0,
      expired: globalStats.expired || 0,
      expiringSoon, // Este sigue calculándose desde página actual por ahora
    };
  };

  const statusCounts = getStatusCounts();
  const filteredSimCards = getFilteredSimCards();

  if (error) {
    return (
      <Alert severity="error">
        Error cargando SIM cards: {error}
        <Button onClick={() => fetchSimCards()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Gestión de SIM Cards
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administra todas las SIM cards del sistema
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchSimCards()}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSimCard}
            size="large"
          >
            Nueva SIM Card
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                {statusCounts.total}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Total SIM Cards
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {statusCounts.active}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Activas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="text.secondary" sx={{ fontWeight: 600 }}>
                {statusCounts.inactive}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Inactivas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {statusCounts.expiringSoon}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Expiran Pronto
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main" sx={{ fontWeight: 600 }}>
                {statusCounts.expired}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Expiradas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar por ICCID, número o carrier..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Estado"
              size="medium"
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Activas</MenuItem>
              <MenuItem value="inactive">Inactivas</MenuItem>
              <MenuItem value="suspended">Suspendidas</MenuItem>
              <MenuItem value="expired">Expiradas</MenuItem>
              <MenuItem value="lost">Perdidas</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* SIM Cards Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SIM Card</TableCell>
                <TableCell>Número / Plan</TableCell>
                <TableCell>Cliente / Dispositivo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Uso de Datos</TableCell>
                <TableCell>Expiración</TableCell>
                <TableCell width={60}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !filteredSimCards.length ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      {searchTerm || statusFilter !== 'all' ? 'No se encontraron SIM cards' : 'No hay SIM cards registradas'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSimCards.map((simCard) => (
                  <SimCardRow
                    key={simCard._id}
                    simCard={simCard}
                    onView={handleView}
                    onEdit={handleEdit}
                    onUpdateStatus={handleUpdateStatus}
                    onSyncWithSimbase={handleSyncWithSimbase}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {simCardsData && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={simCardsData.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
          />
        )}
      </Paper>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add sim card"
        onClick={handleAddSimCard}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' },
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default SimCardsPage;